/**
 * RegionalSalesProblems — "Problem Workshops" card: soft / at-risk
 * workshops (worst pace diff first) with fill bars and a per-row Push
 * action (disabled-CTA until a promote flow exists).
 *
 * Live when the DTO ships `problemWorkshops`; otherwise an em-dash
 * skeleton behind <MockedOverlay> (BON-138).
 */
import { FillBar, MockedOverlay } from '../primitives';

import type { RegionalSalesDto } from './regionalApi';

const EM_DASH = '—';

const SKELETON_ROW_KEYS = ['a', 'b', 'c', 'd'] as const;

function fmtDate(dateIso: string): string {
  return new Date(`${dateIso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function PushButton() {
  return (
    <button
      type="button"
      className="nb-btn"
      disabled
      style={{ height: 22, padding: '0 8px', fontSize: 10.5, opacity: 0.55 }}
      title="Coming soon — push-to-promote ships with the BON-138 data"
    >
      Push
    </button>
  );
}

function LiveRow({ row }: { row: NonNullable<RegionalSalesDto['problemWorkshops']>[number] }) {
  return (
    <tr>
      <td style={{ fontWeight: 600 }}>{fmtDate(row.dateIso)}</td>
      <td>
        {row.venueShort}
        {row.city && <span style={{ color: 'var(--nb-subtle)' }}> · {row.city.split(',')[0]}</span>}
      </td>
      <td className="nb-muted">{row.label}</td>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FillBar sold={row.sold} cap={row.cap} width={60} ariaLabel={`${row.label} fill`} />
          <span className="nb-mono nb-tabular" style={{ fontSize: 10.5, color: 'var(--nb-muted)' }}>
            {row.sold}/{row.cap}
          </span>
        </div>
      </td>
      <td
        className="nb-tabular nb-mono"
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: row.status === 'risk' ? 'var(--nb-risk)' : 'var(--nb-warn)',
        }}
      >
        {row.diffPct}pts
      </td>
      <td>
        <PushButton />
      </td>
    </tr>
  );
}

function SkeletonRow() {
  return (
    <tr>
      <td style={{ fontWeight: 600, color: 'var(--nb-subtle)' }}>{EM_DASH}</td>
      <td className="nb-muted">{EM_DASH}</td>
      <td className="nb-muted">{EM_DASH}</td>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FillBar sold={0} cap={1} width={60} ariaLabel="Fill placeholder" />
          <span className="nb-mono nb-tabular" style={{ fontSize: 10.5, color: 'var(--nb-muted)' }}>
            {EM_DASH}
          </span>
        </div>
      </td>
      <td className="nb-tabular nb-mono" style={{ fontSize: 11, color: 'var(--nb-subtle)' }}>
        {EM_DASH}
      </td>
      <td>
        <PushButton />
      </td>
    </tr>
  );
}

function ProblemsTable({ children }: { children: React.ReactNode }) {
  return (
    <table className="nb-tbl">
      <thead>
        <tr>
          <th>Date</th>
          <th>Venue</th>
          <th>Species</th>
          <th>Fill</th>
          <th>Diff</th>
          <th></th>
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

export function RegionalSalesProblems({
  problems,
}: {
  problems: RegionalSalesDto['problemWorkshops'];
}) {
  return (
    <section className="nb-card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={{ fontSize: 13, letterSpacing: '0.04em', margin: 0 }}>
          Problem Workshops
        </h3>
        <span
          className="nb-pill nb-status-soft"
          style={{ height: 18, fontSize: 9.5, padding: '0 6px' }}
        >
          {problems ? problems.length : EM_DASH} need attention
        </span>
      </div>
      {problems ? (
        <>
          <ProblemsTable>
            {problems.map((row) => (
              <LiveRow key={`${row.dateIso}-${row.venueShort}-${row.label}`} row={row} />
            ))}
          </ProblemsTable>
          {problems.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--nb-subtle)', paddingTop: 8 }}>
              Nothing behind pace — every upcoming workshop is on track.
            </div>
          )}
        </>
      ) : (
        <MockedOverlay reason="Problem-workshop pacing source is unavailable. Tracked in BON-138.">
          <ProblemsTable>
            {SKELETON_ROW_KEYS.map((k) => (
              <SkeletonRow key={k} />
            ))}
          </ProblemsTable>
        </MockedOverlay>
      )}
    </section>
  );
}
